<script lang="ts">
  import type { BuildingType, ImportedCity, ResolvedBuilding } from '$lib/types'
  import { extractPlayerEra, groupByEra, resolveBuilding } from '$lib/catalogue'
  import buildingInfo from '../../../data/buildingInfo.json'

  interface Props {
    importedCity: ImportedCity | null
    onPickup: (resolved: ResolvedBuilding) => void
  }

  const { importedCity, onPickup }: Props = $props()

  let query = $state('')

  // Player-buildable subset. The excluded list mirrors the spec: streets land
  // in Phase 3 alongside road validation; off-grid families and the unknown
  // bucket can't sensibly be placed by clicking on the grid.
  const EXCLUDED = new Set<string>([
    'street',
    'outpost_ship',
    'friends_tavern',
    'off_grid',
    'hub_main',
    'hub_part',
    'impediment',
    'unknown',
  ])

  // Type buckets exposed as chip filters. Each chip rolls up several raw
  // BuildingType values into a single category — players think in terms of
  // "production" not "random_production / cultural_goods_production". The
  // ordering matches the UI label order.
  type CategoryKey =
    | 'residential'
    | 'production'
    | 'goods'
    | 'culture'
    | 'military'
    | 'decoration'
    | 'greatbuilding'
    | 'mainbuilding'

  const CATEGORIES: ReadonlyArray<{ key: CategoryKey; label: string }> = [
    { key: 'residential', label: 'Residential' },
    { key: 'production', label: 'Production' },
    { key: 'goods', label: 'Goods' },
    { key: 'culture', label: 'Culture' },
    { key: 'military', label: 'Military' },
    { key: 'decoration', label: 'Decoration' },
    { key: 'greatbuilding', label: 'GB' },
    { key: 'mainbuilding', label: 'Main' },
  ]

  const TYPE_TO_CATEGORY: Partial<Record<BuildingType, CategoryKey>> = {
    residential: 'residential',
    production: 'production',
    random_production: 'production',
    goods: 'goods',
    cultural_goods_production: 'goods',
    culture: 'culture',
    static_provider: 'culture',
    diplomacy: 'culture',
    military: 'military',
    tower: 'military',
    decoration: 'decoration',
    placement: 'decoration',
    clan_power_production: 'decoration',
    greatbuilding: 'greatbuilding',
    main_building: 'mainbuilding',
  }

  /** 'any' or a `${w}x${h}` key (e.g. '2x3'). The exact-dimension options
   *  are derived from whatever sizes actually appear in the catalogue, so
   *  the dropdown only ever shows real footprints rather than asking the
   *  player to guess what's available. */
  type SizeKey = 'any' | string

  function sizeKey(width: number, height: number): string {
    return `${width}x${height}`
  }

  function parseSizeKey(key: string): { w: number; h: number } | null {
    const m = /^(\d+)x(\d+)$/.exec(key)
    if (!m) return null
    return { w: Number(m[1]), h: Number(m[2]) }
  }

  // Filter state. Category: a single active chip, or null for "All".
  // Single-select feels right for the panel's "I want to find GBs" intent —
  // multi-select would let the player narrow to two-or-more types but in
  // practice the search box covers cross-type lookup, and chipping
  // GB+Residential reads as "show me both" not "show me only those two".
  // Size: 'any' or a specific `${w}x${h}` key.
  let activeCategory: CategoryKey | null = $state(null)
  let sizeFilter: SizeKey = $state('any')

  function pickCategory(key: CategoryKey) {
    // Clicking the currently-active chip drops back to "All", so a chip
    // doubles as its own toggle without needing a separate undo affordance.
    activeCategory = activeCategory === key ? null : key
  }

  function selectAllCategories() {
    activeCategory = null
  }

  /** True when at least one filter is narrowing the list. Used to gate the
   *  "Clear filters" affordance in the empty-match state — it'd be confusing
   *  to offer a clear when there's nothing to clear. */
  const hasActiveFilters = $derived(
    query.trim() !== '' || activeCategory !== null || sizeFilter !== 'any',
  )

  function clearFilters() {
    query = ''
    activeCategory = null
    sizeFilter = 'any'
  }

  // Combine import-local entities with the reference catalogue so we don't
  // miss anything the player owns that hasn't been baked into the slim
  // `buildingInfo.json` snapshot.
  const entries = $derived.by<ResolvedBuilding[]>(() => {
    const seen = new Set<string>()
    const out: ResolvedBuilding[] = []
    if (importedCity) {
      for (const id of Object.keys(importedCity.parsed.cityEntities)) {
        if (seen.has(id)) continue
        const r = resolveBuilding(id, importedCity)
        if (r && !EXCLUDED.has(r.type)) {
          seen.add(id)
          out.push(r)
        }
      }
    }
    const slim = buildingInfo as { id: string }[]
    for (const entry of slim) {
      if (seen.has(entry.id)) continue
      const r = resolveBuilding(entry.id, importedCity ?? undefined)
      if (r && !EXCLUDED.has(r.type)) {
        seen.add(entry.id)
        out.push(r)
      }
    }
    return out.sort((a, b) => a.name.localeCompare(b.name))
  })

  /**
   * Available size options. Compiled from the unique footprints present in
   * the catalogue (sorted by width-asc, then height-asc) so the dropdown
   * only offers sizes that can actually be selected. 0×0 entries (rare
   * catalogue glitches) are skipped — they'd never match a specific filter
   * anyway. Recomputes whenever entries change (which only happens when
   * the imported city changes).
   */
  const sizeOptions = $derived.by<ReadonlyArray<{ key: string; label: string }>>(() => {
    const seen = new Set<string>()
    for (const e of entries) {
      if (e.width <= 0 || e.height <= 0) continue
      seen.add(sizeKey(e.width, e.height))
    }
    return Array.from(seen)
      .sort((a, b) => {
        const pa = parseSizeKey(a)
        const pb = parseSizeKey(b)
        if (!pa || !pb) return 0
        if (pa.w !== pb.w) return pa.w - pb.w
        return pa.h - pb.h
      })
      .map((key) => ({ key, label: key.replace('x', '×') }))
  })

  const filtered = $derived.by(() => {
    const q = query.trim().toLowerCase()
    const sizeMatch = sizeFilter === 'any' ? null : parseSizeKey(sizeFilter)
    return entries.filter((e) => {
      // When a category chip is active, the entry must roll up to it.
      // Entries whose raw type isn't classified (a future game type we
      // haven't bucketed) are excluded under any active category — the
      // player explicitly asked for one bucket and an unmapped entry
      // doesn't belong there. Under "All", they pass through.
      if (activeCategory !== null) {
        const cat = TYPE_TO_CATEGORY[e.type]
        if (cat !== activeCategory) return false
      }
      if (sizeMatch && (e.width !== sizeMatch.w || e.height !== sizeMatch.h)) return false
      if (q && !e.name.toLowerCase().includes(q)) return false
      return true
    })
  })

  // Search flattens the list — when the player is hunting by name, era
  // headers add visual noise. Filter chips keep the grouped view because
  // narrowing by category/size still leaves multiple eras worth scanning.
  const groupedView = $derived(query.trim() === '')
  const playerEra = $derived(extractPlayerEra(importedCity))
  const eraGroups = $derived(groupByEra(filtered, (e) => e.id, playerEra))

  /** Cap on items rendered across all groups, mirroring the flat view's
   *  `slice(0, 200)`. We keep groups whole until the budget runs out, then
   *  truncate the last surviving group. Players hunting deeper into the
   *  list narrow with search/filters anyway. */
  const RENDER_CAP = 200
  const cappedGroups = $derived.by(() => {
    let remaining = RENDER_CAP
    const out: { era: string; label: string; isPlayerEra: boolean; entries: ResolvedBuilding[] }[] = []
    for (const group of eraGroups) {
      if (remaining <= 0) break
      if (group.entries.length <= remaining) {
        out.push(group)
        remaining -= group.entries.length
      } else {
        out.push({ ...group, entries: group.entries.slice(0, remaining) })
        remaining = 0
      }
    }
    return out
  })
</script>

<div class="flex flex-col gap-2 p-2">
  <input
    type="search"
    bind:value={query}
    class="rounded border border-input-border bg-input-bg px-2 py-1 text-sm text-input-text placeholder-input-placeholder focus:border-accent focus:outline-none"
    placeholder="Search catalogue…"
  />

  <div class="flex flex-wrap items-center gap-1" role="group" aria-label="Type filter">
    <button
      type="button"
      class="rounded border px-2 py-0.5 text-[11px] transition {activeCategory === null
        ? 'border-accent bg-accent text-white'
        : 'border-transparent bg-surface-card-recessed text-text-primary hover:bg-text-primary/10'}"
      aria-pressed={activeCategory === null}
      onclick={selectAllCategories}
      title="Show all types"
    >
      All
    </button>
    {#each CATEGORIES as cat (cat.key)}
      <button
        type="button"
        class="rounded border px-2 py-0.5 text-[11px] transition {activeCategory === cat.key
          ? 'border-accent bg-accent text-white'
          : 'border-transparent bg-surface-card-recessed text-text-primary hover:bg-text-primary/10'}"
        aria-pressed={activeCategory === cat.key}
        onclick={() => pickCategory(cat.key)}
      >
        {cat.label}
      </button>
    {/each}
  </div>

  <select
    bind:value={sizeFilter}
    class="rounded border border-input-border bg-input-bg px-2 py-1 text-xs text-input-text focus:border-accent focus:outline-none"
    aria-label="Size filter"
  >
    <option value="any">Any size</option>
    {#each sizeOptions as opt (opt.key)}
      <option value={opt.key}>{opt.label}</option>
    {/each}
  </select>

  {#if filtered.length === 0}
    <div class="flex flex-col items-start gap-2 px-1 py-3 text-xs text-text-secondary">
      <p>
        {#if hasActiveFilters}
          No buildings match those filters.
        {:else}
          No buildings to show.
        {/if}
      </p>
      {#if hasActiveFilters}
        <button
          type="button"
          class="rounded border border-border-rule bg-surface-card-recessed px-2 py-1 text-[11px] text-text-primary hover:border-accent/50"
          onclick={clearFilters}
        >
          Clear filters
        </button>
      {/if}
    </div>
  {:else if groupedView}
    <div class="flex max-h-[55vh] flex-col gap-2 overflow-y-auto pr-1 text-sm">
      {#each cappedGroups as group (group.era)}
        <section>
          <header
            class="sticky top-0 z-[1] flex items-baseline justify-between gap-2 bg-surface-card/95 px-1 py-1 backdrop-blur-sm"
          >
            <h4
              class="text-[11px] font-semibold uppercase tracking-wide {group.isPlayerEra
                ? 'text-accent-bright'
                : 'text-text-secondary'}"
            >
              {group.label}{#if group.isPlayerEra}
                <span class="ml-1 text-[10px] font-normal lowercase">(your era)</span>
              {/if}
            </h4>
            <span class="text-[10px] text-text-muted">{group.entries.length}</span>
          </header>
          <ul class="flex flex-col gap-1">
            {#each group.entries as entry (entry.id)}
              <li
                class="flex items-center justify-between gap-2 rounded border border-border-rule bg-surface-card-recessed px-2 py-1.5 hover:border-accent/50"
              >
                <div class="min-w-0 flex-1">
                  <p class="truncate text-text-primary">{entry.name}</p>
                  <p class="text-[11px] text-text-muted">
                    {entry.width}×{entry.height} · {entry.type}
                  </p>
                </div>
                <button
                  type="button"
                  class="rounded bg-accent px-2 py-1 text-[11px] text-white hover:bg-accent-bright"
                  onclick={() => onPickup(entry)}
                  title="Click, then click on the grid to place"
                >
                  place
                </button>
              </li>
            {/each}
          </ul>
        </section>
      {/each}
      {#if filtered.length > RENDER_CAP}
        <p class="px-1 pt-1 text-[11px] text-text-muted">
          Showing first {RENDER_CAP} of {filtered.length} — narrow with search or filters.
        </p>
      {/if}
    </div>
  {:else}
    <ul class="flex max-h-[55vh] flex-col gap-1 overflow-y-auto pr-1 text-sm">
      {#each filtered.slice(0, RENDER_CAP) as entry (entry.id)}
        <li
          class="flex items-center justify-between gap-2 rounded border border-border-rule bg-surface-card-recessed px-2 py-1.5 hover:border-accent/50"
        >
          <div class="min-w-0 flex-1">
            <p class="truncate text-text-primary">{entry.name}</p>
            <p class="text-[11px] text-text-muted">
              {entry.width}×{entry.height} · {entry.type}
            </p>
          </div>
          <button
            type="button"
            class="rounded bg-accent px-2 py-1 text-[11px] text-white hover:bg-accent-bright"
            onclick={() => onPickup(entry)}
            title="Click, then click on the grid to place"
          >
            place
          </button>
        </li>
      {/each}
    </ul>
    {#if filtered.length > RENDER_CAP}
      <p class="px-1 text-[11px] text-text-muted">Showing first {RENDER_CAP} of {filtered.length}</p>
    {/if}
  {/if}
</div>
